import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  SideBarDef,
} from "ag-grid-community";
import { useNavigate } from "react-router";

import { AgGridReact } from "ag-grid-react";
import { Dialog } from "@mui/material";
import _ from "lodash";
import { toast } from "react-toastify";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";

import { Box, HStack, useGetCompanyName } from "../../components/utils";
import GenericTableHeader from "../../shared/TableHeader";
import { ButtonSize, IconCTAButton, SecondaryCTAButton } from "../quickRound/CTAButtonComponents";
import QuickSearch from "../rounds/QuickSearch";
import { useAuthStore } from "../../store";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../dashboardPage/AgGridCacheQuery";
import { tableCellStyle } from "../../components/TableComponent";
import { useAgGridTableState } from "../../store/agGridTableStore";
import SearchInput from "../../components/shared/SearchInput";
import { AdditionalShares, AllocationMethod, SecurityDetails } from "./AntiDilutionSecurityAgGridComponent";
import { AntiDilutionAllocationType, AntiDilutionSecurity, InvestorDetails, PreviewQuickRoundResponseModel } from "../../types/previewQuickRound";
import { InstrumentClass } from "../../types/DashboardModel";
import Tooltip from "../../components/shared/Tooltip";
import { useUpdateAntDilutionDetails } from "../../queries/transactionRound";
import { QuickRoundTransactionModel } from "./RoundTransactionModel";
import { queryClient } from "../../queries";
import { ShareholderDetails } from "../../types/SecurityModel";
import { getIndividualShareholderOverview } from "../../api/shareholders";

type ShareholderSecurityHolding = ShareholderDetails & {
  numberOfShares?: number;
  newNumberOfShares?: number;
  finalFdbShares?: number;
};

function AGGridAntiDilutionShareholdersTable({
  investors,
  setInvestors,
  instrumentClassId,
  ratio,
  originalRatio,
  gridRefAntiDilutionShareholder,
  allocationMethod,
  updateRatio,
  mode,
}: {
  investors: InvestorDetails[];
  setInvestors: Function;
  instrumentClassId: string;
  ratio: number;
  originalRatio: number;
  gridRefAntiDilutionShareholder: React.RefObject<AgGridReact<any>>
  allocationMethod: string;
  updateRatio: (val: number) => void;
  mode: string;
}) {
  const currencySymbol = getCurrencySymbol();
  const companyName = useGetCompanyName();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const gridApi = useRef<any>(null);

  const displayedRowCount = gridApi.current?.api.getDisplayedRowCount();

  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );
  const agTableStore = useAgGridTableState();

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        shareHolderAgGridCaptableQuickRoundColumnModel: columnState,
        shareHolderAgGridCaptableQuickRoundFilterModel: filterState,
      },
    });
  };

  const cin = "";

  const componentsRegistery = useMemo(
    () => ({
      securityDetails: memo(SecurityDetails),
      allocationMethod: memo(AllocationMethod),
      additionalShares: memo(AdditionalShares),
    }),
    []
  );

  const navigate = useNavigate();

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Shareholder Name",
        field: "shareholderName",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.value,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        pinned: "left",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Currently Holding Shares",
        field: "shares",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.value,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "shares",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "New Anti-Dilution Shares",
        field: "additionalShares",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.value,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "additionalShares",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        editable:
          allocationMethod === AntiDilutionAllocationType.IssueShares && mode === "Update",
      },
      {
        headerName: "Final FDB Shares",
        field: "finalFdbShares",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.value,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "finalFdbShares",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
    ],
    [allocationMethod]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchShareholderData = async () => {
        // Show loading overlay when fetching starts
      if (gridRefAntiDilutionShareholder.current?.api) {
        gridRefAntiDilutionShareholder.current.api.showLoadingOverlay();
      }
      const results = await Promise.all(
        investors.map(async (shareholder) => {
          const data = await getIndividualShareholderOverview({
            queryKey: ["", shareholder.investorId],
            meta: undefined
          });
          const numberOfShares = _.sumBy(
            data.shareholderSecurities.filter(
              (security) => security.securityClassId === instrumentClassId
            ),
            (security) => security.numberOfShares
          );
          const additionalShares =
            allocationMethod === AntiDilutionAllocationType.None
            ? 0
            : allocationMethod === AntiDilutionAllocationType.IssueShares ?
                shareholder.additionalShares ??
                Math.floor((shareholder.numberOfShares || 0) * (ratio - 1)) :
                Math.floor((shareholder.numberOfShares || 0) * (ratio - 1));
          const finalShares = numberOfShares + additionalShares;
          const finalFdbShares = Math.floor(finalShares * originalRatio);
          return {
            ...shareholder,
            investorName: data.shareholderProfile.name,
            numberOfShares,
            additionalShares,
            finalFdbShares,
            allocationMethod,
          };
        })
      );
      if (isMounted) {
        if (!_.isEqual(investors, results)) {
          setInvestors(results);
          const totalFDbShares = _.sumBy(results, (investor) => investor.finalFdbShares || 0);
          const totalShares = _.sumBy(results, (investor) => investor.numberOfShares || 0);
          const newRatio = totalShares === 0 ? 0 : (totalFDbShares / totalShares);
          updateRatio(newRatio);
        }
      }
      gridRefAntiDilutionShareholder.current?.api.hideOverlay();
    };

    if (investors.length > 0) {
      fetchShareholderData();
    }

    return () => {
      isMounted = false;
    };
  }, [investors, ratio, instrumentClassId, allocationMethod]);

  useEffect(() => {
    const updatedInvestors = investors.map((investor) => {
      const numberOfShares = investor.numberOfShares || 0;
      const additionalShares =
        allocationMethod === AntiDilutionAllocationType.None
        ? 0
        : allocationMethod === AntiDilutionAllocationType.IssueShares ?
            investor.additionalShares ?? Math.floor((investor.numberOfShares || 0) * (ratio - 1)) :
            Math.floor((investor.numberOfShares || 0) * (ratio - 1));
      const finalShares = numberOfShares + additionalShares;
      const finalFdbShares = Math.floor(finalShares * originalRatio);
      return {
        ...investor,
        additionalShares,
        finalFdbShares,
      };
    });
    if (!_.isEqual(investors, updatedInvestors)) {
      setInvestors(updatedInvestors);
      const totalFDbShares = _.sumBy(updatedInvestors, (investor) => investor.finalFdbShares || 0);
      const totalShares = _.sumBy(updatedInvestors, (investor) => investor.numberOfShares || 0);
      const newRatio = totalShares === 0 ? 0 : (totalFDbShares / totalShares);
      updateRatio(newRatio);
    }
  }, [ratio, allocationMethod]);

  const gridOptions = {
    suppressRowVirtualisation: true, // Add this line to disable row virtualization
    paginationAutoPageSize: false, // Optional: Set to false if using custom pagination
    suppressScrollOnNewData: true, // Optional: Set to true to prevent scrolling to the top on
    enableCellChangeFlash: true,
  };

  const currencyType = getCurrencyType();

  const rowData = useMemo(
    () =>
      investors?.map((shareholder) => ({
        investorId: shareholder.investorId,
        shareholderName: shareholder.investorName,
        shares: shareholder.numberOfShares || 0,
        additionalShares: shareholder.additionalShares,
        numberOfShares: shareholder.numberOfShares,
        finalFdbShares: shareholder.finalFdbShares,
        currencySymbol,
        currencyType,
        allocationMethod: shareholder.allocationMethod,
      })),
    [investors]
  );
  function handleCellValueChanged(event: any) {
    const rowIndex = event.rowIndex;
    const updatedRowData = event.data;

    event.data.finalFdbShares =
      Math.floor(event.data.numberOfShares + event.data.additionalShares) * originalRatio;
    updatedRowData.finalFdbShares =
      Math.floor(updatedRowData.numberOfShares + updatedRowData.additionalShares) * originalRatio;

    gridRefAntiDilutionShareholder.current?.api.applyTransaction({
      update: [{ rowIndex, data: updatedRowData }],
    });
    gridRefAntiDilutionShareholder.current?.api.refreshCells({ force: true });
  }

  function setTableHeight() {
    if (displayedRowCount === 1) {
      return (displayedRowCount + 2.75) * 60;
    } else if (displayedRowCount === 2) {
      return (displayedRowCount + 2.68) * 60;
    } else if (displayedRowCount === 3) {
      return (displayedRowCount + 3) * 60;
    } else if (displayedRowCount === 4) {
      return (displayedRowCount + 2.55) * 60;
    } else if (displayedRowCount === 5) {
      return (displayedRowCount + 2.48) * 60;
    } else if (displayedRowCount === 6) {
      return (displayedRowCount + 2.42) * 60;
    } else if (displayedRowCount === 7) {
      return (displayedRowCount + 2.35) * 60;
    } else if (displayedRowCount === 8) {
      return (displayedRowCount + 2.28) * 60;
    } else if (displayedRowCount === 9) {
      return (displayedRowCount + 2.22) * 60;
    } else {
      return 10 * 60;
    }
  }

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilterAndColumn();
  };

  const { mutate: updateAntDilutionDetails } = useUpdateAntDilutionDetails();

  return (
    <div className="bg-white border rounded-md shadow-box border-borderColor py-5">
      <HStack className="flex-col items-start justify-start py-2 lg:flex-row lg:justify-between lg:items-center">
        <GenericTableHeader
          heading={"Shareholders"}
          subHeading={"Anti-dilution shares based on allocation method parameters"}
        />
        <HStack className="items-center justify-between w-full gap-4 px-8 py-2 lg:w-auto lg:justify-end">
          <SearchInput
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
          <HStack className="gap-4">
            <IconCTAButton
              value={"Columns"}
              onClick={() => openToolPanel("columns")}
              iconName={"fluent:column-triple-edit-20-regular"}
              className={`px-4 font-medium items-center flex flex-row ${
                isColumnOpen ? "text-orange-501" : "text-gray-400"
              }`}
            />
            <IconCTAButton
              value={"Filters"}
              onClick={() => openToolPanel("filters")}
              iconName={"material-symbols:filter-alt"}
              className={`px-4 font-medium items-center flex flex-row ${
                isFilterOpen ? "text-orange-501" : "text-gray-400"
              }`}
            />
          </HStack>
        </HStack>
      </HStack>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: setTableHeight(),
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material "
        >
          <AgGridReact
            ref={gridRefAntiDilutionShareholder}
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              // Store the grid API referen
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysShowHorizontalScroll={false}
            alwaysMultiSort
            animateRows={true}
            defaultColDef={defaultColDef}
            onCellValueChanged={(event) => handleCellValueChanged(event)}
            onFilterChanged={onAgGridFilterChanged}
            onColumnEverythingChanged={uploadFilterAndColumn}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={false}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            onColumnResized={uploadFilterAndColumn}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            singleClickEdit={true}
            rowClass={"border-t border-dashed "}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
            gridOptions={gridOptions}
            stopEditingWhenCellsLoseFocus={true}
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>
    </div>
  );
}

export default AGGridAntiDilutionShareholdersTable;
